import { config } from '../config'
import { adminAuth } from './adminAuth'
import type { AdminSurveyStats, AdminResultItem, AdminRespondent } from '../types/admin'
import type { Survey } from '../types/survey'

const BASE = config.apiBaseUrl

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = adminAuth.getToken()
  const response = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  })

  if (!response.ok) {
    let err = `HTTP ${response.status}`
    try {
      const body = await response.json()
      err = body.error || err
    } catch {
      // ignore
    }
    throw new Error(err)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const adminApi = {
  async login(password: string): Promise<string> {
    const res = await request<{ token: string }>('/v1/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
    return res.token
  },

  listSurveys(): Promise<Survey[]> {
    return request<Survey[]>('/v1/admin/surveys')
  },

  getSurvey(surveyId: string): Promise<Survey> {
    return request<Survey>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}`)
  },

  createSurvey(payload: { id: string; title: string; description?: string; definition: Record<string, unknown>; status?: string }): Promise<Survey> {
    return request<Survey>('/v1/admin/surveys', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  updateSurvey(surveyId: string, payload: { title?: string; description?: string; definition?: Record<string, unknown>; status?: string; enabled?: boolean }): Promise<Survey> {
    return request<Survey>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  async getResults(surveyId: string): Promise<AdminResultItem[]> {
    const res = await request<{ items?: AdminResultItem[] } | AdminResultItem[]>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}/results?page=1&pageSize=200`)
    return Array.isArray(res) ? res : (res.items || [])
  },

  getStats(surveyId: string): Promise<AdminSurveyStats> {
    return request<AdminSurveyStats>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}/stats`)
  },

  listRespondents(surveyId: string): Promise<AdminRespondent[]> {
    return request<AdminRespondent[]>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}/respondents`)
  },

  getQrPngUrl(surveyId: string, token?: string): string {
    const tokenPart = token ? `&token=${encodeURIComponent(token)}` : ''
    return `${BASE}/v1/surveys/${encodeURIComponent(surveyId)}/qr?frontendUrl=${encodeURIComponent(window.location.origin)}${tokenPart}`
  },

  getSurveyLink(surveyId: string, token?: string): string {
    const tokenPart = token ? `?token=${encodeURIComponent(token)}` : ''
    return `${window.location.origin}/#/survey/${encodeURIComponent(surveyId)}${tokenPart}`
  },

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return request<void>('/v1/admin/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  },

  deleteSurvey(surveyId: string): Promise<void> {
    return request<void>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}`, { method: 'DELETE' })
  },

  resetSurvey(surveyId: string): Promise<{ surveyId: string; reset: boolean }> {
    return request<{ surveyId: string; reset: boolean }>(`/v1/admin/surveys/${encodeURIComponent(surveyId)}/reset`, { method: 'POST' })
  },
}
